import React, { useState, useEffect, useMemo } from 'react';
import { AppointmentType, LeaveEntry, PublicHoliday } from '@techgitter/contracts';
import { WorkItem } from '../workitems/workItemTypes';
import { createAppointment, updateAppointment, CreateAppointmentDto } from '../../services/appointmentDataService';
import { getAllConflicts } from './utils/conflictUtils';
import { formatDateStr } from './utils/calendarUtils';
import { SidePanel } from '../panel/SidePanel';
import { FormField } from '../panel/FormField';
import { SmartSelect, DateField, TimeField } from '../dropdown';
import { makeTechnicianRenderItem } from './renderTechnicianOption';
import { buildUserColorIndex } from '../../utils/userColorIndex';
import { useUsers } from '../../hooks/useUsers';

const APPOINTMENT_TYPES: AppointmentType[] = ['Instore', 'Onsite', 'Phone Call', 'Remote Support'];
const DEFAULT_DURATION_MINUTES = 30;
const ONSITE_DURATION_MINUTES = 60;

/**
 * Get the next 15-minute time slot from now (e.g. 09:17 → 09:30, 09:45 → 10:00).
 */
function getNext15MinSlot(): string {
  const now = new Date();
  const minutes = now.getMinutes();
  const next = Math.ceil((minutes + 1) / 15) * 15;
  const d = new Date(now);
  d.setMinutes(next, 0, 0);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export interface AppointmentSidePanelDefaults {
  technicianName: string;
  dateStr: string;
  hour: number;
  minute: number;
}

interface AppointmentSidePanelProps {
  /** Pre-filled from a time slot click. Null when opened via the toolbar button. */
  defaults: AppointmentSidePanelDefaults | null;
  /** Existing appointment to edit. Null for create mode. */
  editingAppointment?: WorkItem | null;
  /** Signed-in user's display name — used as default technician */
  currentUser: string;
  /** Known technician names for autocomplete */
  technicians: string[];
  appointments: WorkItem[];
  leaveEntries: LeaveEntry[];
  holidays: PublicHoliday[];
  onClose: () => void;
  onCreated: () => void;
}

export const AppointmentSidePanel: React.FC<AppointmentSidePanelProps> = ({
  defaults,
  editingAppointment,
  currentUser,
  technicians,
  appointments,
  leaveEntries,
  holidays,
  onClose,
  onCreated,
}) => {
  const { users: cachedUsers } = useUsers();
  const renderTechnicianItem = useMemo(
    () => makeTechnicianRenderItem(buildUserColorIndex(cachedUsers)),
    [cachedUsers],
  );
  const [title, setTitle] = useState('');
  const [type, setType] = useState<AppointmentType>('Instore');
  const [technicianName, setTechnicianName] = useState('');
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [notes, setNotes] = useState('');
  const [durationMinutes, setDurationMinutes] = useState(DEFAULT_DURATION_MINUTES);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isEditing = !!editingAppointment;

  // Populate from defaults or editing appointment
  useEffect(() => {
    setError(null);

    if (editingAppointment) {
      setTitle(editingAppointment.title);
      setType((editingAppointment.appointmentType as AppointmentType) || 'Instore');
      setTechnicianName(editingAppointment.userFullName || '');
      setCustomerName(editingAppointment.customerName || '');
      setNotes(editingAppointment.notes || '');

      if (editingAppointment.startAt) {
        const s = new Date(editingAppointment.startAt);
        setDate(formatDateStr(s));
        const pad = (n: number) => String(n).padStart(2, '0');
        setTime(`${pad(s.getHours())}:${pad(s.getMinutes())}`);
      }
      if (editingAppointment.startAt && editingAppointment.endAt) {
        const dur = Math.round((new Date(editingAppointment.endAt).getTime() - new Date(editingAppointment.startAt).getTime()) / 60_000);
        setDurationMinutes(Math.max(15, dur));
      } else {
        setDurationMinutes(DEFAULT_DURATION_MINUTES);
      }
    } else if (defaults) {
      setTitle('');
      setType('Instore');
      setCustomerName('');
      setNotes('');
      setDurationMinutes(DEFAULT_DURATION_MINUTES);
      setTechnicianName(defaults.technicianName);
      setDate(defaults.dateStr);
      setTime(`${String(defaults.hour).padStart(2, '0')}:${String(defaults.minute).padStart(2, '0')}`);
    } else {
      setTitle('');
      setType('Instore');
      setCustomerName('');
      setNotes('');
      setDurationMinutes(DEFAULT_DURATION_MINUTES);
      setTechnicianName(currentUser);
      setDate(formatDateStr(new Date()));
      setTime(getNext15MinSlot());
    }
  }, [defaults, editingAppointment, currentUser]);

  // Auto-adjust duration when type changes
  const handleTypeChange = (newType: AppointmentType) => {
    setType(newType);
    setDurationMinutes(newType === 'Onsite' ? ONSITE_DURATION_MINUTES : DEFAULT_DURATION_MINUTES);
  };

  // Compute start/end — local inputs converted to UTC ISO strings for Dataverse
  const startAt = useMemo(() => {
    if (!date || !time) return '';
    return new Date(`${date}T${time}:00`).toISOString();
  }, [date, time]);

  const endAt = useMemo(() => {
    if (!startAt) return '';
    const d = new Date(startAt);
    d.setMinutes(d.getMinutes() + durationMinutes);
    return d.toISOString();
  }, [startAt, durationMinutes]);

  // Preview conflicts
  const previewConflicts = useMemo(() => {
    if (!technicianName || !startAt || !endAt) return [];
    const preview = {
      guid: '__preview__',
      id: '',
      type: 'appointment' as const,
      title,
      userFullName: technicianName,
      userEmailAddress: '',
      displayIndex: 0,
      status: 'Scheduled',
      customerId: '',
      customerName: customerName,
      customerEmail: '',
      customerPhone: '',
      customerMobile: '',
      createdAt: '',
      updatedAt: '',
      notes: '',
      createdOn: '',
      closedDate: '',
      jobCreatedAt: '',
      jobUpdatedAt: '',
      jobPriority: '',
      description: '',
      category: '',
      startAt,
      endAt,
      appointmentType: type,
    } as WorkItem;
    return getAllConflicts(preview, appointments, leaveEntries, holidays);
  }, [technicianName, startAt, endAt, title, customerName, type, appointments, leaveEntries, holidays]);

  const handleSubmit = async () => {
    if (!title.trim() || !technicianName.trim() || !startAt || !endAt) return;

    setIsSubmitting(true);
    setError(null);

    try {
      if (isEditing) {
        await updateAppointment(editingAppointment!.guid, {
          title: title.trim(),
          type,
          startAt,
          endAt,
          assignedUserFullName: technicianName.trim(),
          customerName: customerName.trim() || undefined,
          notes: notes.trim() || undefined,
        });
      } else {
        const dto: CreateAppointmentDto = {
          title: title.trim(),
          type,
          startAt,
          endAt,
          assignedUserFullName: technicianName.trim(),
          customerName: customerName.trim() || undefined,
          notes: notes.trim() || undefined,
        };
        await createAppointment(dto);
      }
      onCreated();
    } catch (err: any) {
      setError(err.message || `Failed to ${isEditing ? 'update' : 'create'} appointment`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const isValid = !!(title.trim() && technicianName.trim() && date && time);

  return (
    <SidePanel
      title={isEditing ? 'Edit Appointment' : 'Schedule Appointment'}
      type="appointment"
      submitLabel={
        isSubmitting
          ? (isEditing ? 'Saving…' : 'Scheduling…')
          : (isEditing ? 'Save' : 'Schedule')
      }
      isValid={isValid && !isSubmitting}
      onClose={onClose}
      onSubmit={handleSubmit}
    >
      {previewConflicts.length > 0 && (
        <div className="side-panel__alert side-panel__alert--warning">
          {previewConflicts.map(c => c.message).join('. ')}
        </div>
      )}

      {error && <div className="side-panel__alert side-panel__alert--error">{error}</div>}

      <FormField label="Title *">
        <input autoFocus value={title} onChange={(e) => setTitle(e.target.value)} />
      </FormField>

      <SmartSelect
        label="Technician *"
        value={technicianName || null}
        options={technicians.map((t) => ({ value: t, label: t }))}
        onChange={(val) => setTechnicianName(val ?? '')}
        placeholder="Select technician…"
        renderItem={renderTechnicianItem}
        disabled={!!defaults}
      />

      <div className="side-panel__field-row">
        <DateField
          label="Date *"
          value={date || null}
          onChange={(iso) => setDate(iso ?? '')}
          disabled={!!defaults}
        />
        <TimeField
          label="Time *"
          value={time || null}
          onChange={(hhmm) => setTime(hhmm ?? '')}
          disabled={!!defaults}
        />
      </div>

      <SmartSelect
        label="Type"
        value={type}
        options={APPOINTMENT_TYPES.map((t) => ({ value: t, label: t }))}
        onChange={(val) => {
          if (val) handleTypeChange(val as AppointmentType);
        }}
      />

      <FormField label="Duration (minutes)">
        <input
          type="number"
          min={15}
          step={15}
          value={durationMinutes}
          onChange={(e) => setDurationMinutes(Math.max(15, Number(e.target.value)))}
        />
      </FormField>

      <FormField label="Customer Name (optional)">
        <input
          value={customerName}
          onChange={(e) => setCustomerName(e.target.value)}
          placeholder="Enter name or leave blank"
        />
      </FormField>

      <FormField label="Notes (optional)">
        <textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} />
      </FormField>
    </SidePanel>
  );
};
